/* eslint-disable complexity */
import React, { Component } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import DatePicker from 'react-datepicker-legacy';

import { Timezone } from '@scale/shared/defaults';
// TODO: Migrate module so that you don’t import from the dashboard project in a global module
// eslint-disable-next-line restrict-imports/restrict-imports
import { changeTimezone } from 'dashboard/lib/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
const DATE_FORMAT = 'M/DD/YY';
const DATE_TIME_FORMAT = 'M/DD/YY H:mm';

type Props = {
  to?: moment.Moment;
  from?: moment.Moment;
  onToggleTimezoneModal?: () => void;
  setTo: (to: moment.Moment) => void;
  setFrom: (to: moment.Moment) => void;
  timezone?: Timezone;
  className?: string;
  small?: boolean;
  fromLabel?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  showTimeSelect?: boolean;
  minDate?: moment.Moment | null;
  maxDate?: moment.Moment | null;
  showTimezoneLabel?: boolean;
  popperPlacement?: string;
};

export default class DateRangePicker extends Component<Props> {
  get fromLabel() {
    if (this.props.fromLabel) {
      return this.props.fromLabel;
    }

    return 'FROM';
  }

  renderCustomCSS() {
    const dateWidth = this.props.showTimeSelect ? '90px' : '65px';
    return (
      <style jsx global>{`
        .date-range-picker__label {
          justify-content: center;
          align-items: center;
          font-size: 12px;
          margin: auto 20px;
          text-transform: uppercase;
        }

        .date-range-picker__label:first-child {
          margin: auto 20px auto 0;
        }

        .date-range-picker__date {
          cursor: pointer;
          color: var(--color-form-input);
          padding: 10px 0;
          width: ${dateWidth};
          align-items: center;
          font-size: 13px;
          border-radius: 8px;
          background: transparent;
          border: 0;
          text-align: center;
          appearance: none;
          margin: 0;
        }

        .date-picker.small .date-range-picker__date {
          padding: 6px 0;
        }

        .date-range-picker__date:focus {
          border-color: #ccc;
          outline: 0;
        }

        .react-datepicker-popper {
          z-index: 1000;
        }

        .react-datepicker {
          box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.15);
          border: 0;
          font-family: var(--font-family);
        }
        .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
          border-bottom-color: #222228;
        }
        .react-datepicker__header {
          background: #222228;
          border: 0;
        }

        .react-datepicker__header .react-datepicker__current-month {
          color: white;
        }
        .react-datepicker__header .react-datepicker__day-names {
          margin-top: 10px;
        }
        .react-datepicker__header .react-datepicker__day-name {
          color: white;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__day--in-range {
          background: #2773ff;
          background-color: #2773ff !important;
        }
      `}</style>
    );
  }

  render() {
    const { small, style, setFrom, from, to, setTo, className, timezone, onToggleTimezoneModal } =
      this.props;
    const today = changeTimezone(moment(), timezone ?? Timezone.UTC);
    const maxDate = this.props.maxDate || today;
    const noMax = this.props.maxDate === null;
    const minDate = this.props.minDate || null;
    let timezoneLabel = 'UTC';
    if (timezone === Timezone.Pacific) {
      timezoneLabel = 'PST/PDT';
    } else if (timezone === Timezone.Local) {
      const timeOffset = new Date().getTimezoneOffset() / 60;
      let localGmt;
      if (timeOffset > 0) {
        localGmt = `-${timeOffset}`;
      } else if (timeOffset < 0) {
        localGmt = `+${Math.abs(timeOffset)}`;
      }
      timezoneLabel = `GMT${localGmt}`;
    }
    // default to date to today
    if (!to) {
      setTo(moment());
    }

    return (
      <div className={classnames('date-picker', { small }, className)} style={style}>
        <div className="date-range-picker__container">
          {/* <div className="date-range-picker__label">{this.fromLabel}</div> */}
          <FontAwesomeIcon
            icon="calendar"
            style={{ margin: '0 6px 0 14px', color: 'var(--color-primary)' }}
          />
          <DatePicker
            dateFormat={this.props.showTimeSelect ? DATE_TIME_FORMAT : DATE_FORMAT}
            onChange={setFrom}
            selected={minDate && from && from < minDate ? minDate : from}
            disabled={this.props.disabled || false}
            placeholderText={from ? undefined : 'mm/dd/yy'}
            minDate={minDate || undefined}
            maxDate={moment(to)}
            className="date-range-picker__date"
            showTimeSelect={this.props.showTimeSelect}
            popperPlacement={this.props.popperPlacement}
          />
          <svg
            className="arrow-right"
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.9062 8.18134L19.2937 6.79384C19.8812 6.20634 20.8313 6.20634 21.4125 6.79384L33.5625 18.9376C34.15 19.5251 34.15 20.4751 33.5625 21.0563L21.4125 33.2063C20.825 33.7938 19.875 33.7938 19.2937 33.2063L17.9062 31.8188C17.3125 31.2251 17.325 30.2563 17.9312 29.6751L25.4625 22.5001H7.5C6.66875 22.5001 6 21.8313 6 21.0001V19.0001C6 18.1688 6.66875 17.5001 7.5 17.5001H25.4625L17.9312 10.3251C17.3187 9.74384 17.3062 8.77509 17.9062 8.18134Z" />
          </svg>
          <DatePicker
            dateFormat={this.props.showTimeSelect ? DATE_TIME_FORMAT : DATE_FORMAT}
            onChange={setTo}
            selected={to}
            minDate={moment(from)}
            disabled={this.props.disabled || false}
            maxDate={noMax ? undefined : maxDate}
            className="date-range-picker__date"
            showTimeSelect={this.props.showTimeSelect}
            popperPlacement={this.props.popperPlacement}
          />
          {this.props.showTimezoneLabel !== false ? (
            <span className="utc" onClick={onToggleTimezoneModal && onToggleTimezoneModal}>
              {timezoneLabel}
            </span>
          ) : (
            <div className="hsp__2" />
          )}
        </div>
        <style jsx>{`
          .date-picker.top-filters .date-range-picker__container {
            padding-left: 3px;
            height: 32px;
            border: 0;
            box-sizing: border-box;
          }

          .date-range-picker__container {
            align-items: center;
            display: flex;
            border-radius: 8px;
            background: var(--bg-form-input);
            padding-right: 6px;
          }

          .arrow-right {
            width: 12px;
            height: 12px;
            display: block;
            flex-shrink: 0;
            fill: var(--color-primary);
            padding-left: 2px;
          }

          .date-picker {
            display: flex;
            align-items: center;
          }

          .utc {
            color: #666;
            font-size: 12px;
            margin-right: 10px;
            letter-spacing: 0.3px;
          }
        `}</style>

        <style jsx global>{`
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle,
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle,
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow {
            margin-left: -8px;
            position: absolute;
          }
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle,
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle,
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow,
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before,
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before,
          .react-datepicker__year-read-view--down-arrow::before,
          .react-datepicker__month-read-view--down-arrow::before {
            box-sizing: content-box;
            position: absolute;
            border: 8px solid transparent;
            height: 0;
            width: 1px;
          }
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before,
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before,
          .react-datepicker__year-read-view--down-arrow::before,
          .react-datepicker__month-read-view--down-arrow::before {
            content: '';
            z-index: -1;
            border-width: 8px;
            left: -8px;
            border-bottom-color: #aeaeae;
          }

          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
            top: 0;
            margin-top: -8px;
          }
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle,
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before {
            border-top: none;
            border-bottom-color: #f0f0f0;
          }
          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before {
            top: -1px;
            border-bottom-color: #aeaeae;
          }

          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle,
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow {
            bottom: 0;
            margin-bottom: -8px;
          }
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle,
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow,
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before,
          .react-datepicker__year-read-view--down-arrow::before,
          .react-datepicker__month-read-view--down-arrow::before {
            border-bottom: none;
            border-top-color: #fff;
          }
          .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before,
          .react-datepicker__year-read-view--down-arrow::before,
          .react-datepicker__month-read-view--down-arrow::before {
            bottom: -1px;
            border-top-color: #aeaeae;
          }

          .react-datepicker {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            font-size: 0.8rem;
            background-color: #fff;
            color: #000;
            border: 1px solid #aeaeae;
            border-radius: 0.3rem;
            display: inline-block;
            position: relative;
          }

          .react-datepicker__triangle {
            position: absolute;
            left: 50px;
          }

          .react-datepicker-popper[data-placement^='bottom'] {
            margin-top: 12px;
          }

          .react-datepicker__header {
            text-align: center;
            background-color: #f0f0f0;
            border-bottom: 1px solid #aeaeae;
            border-top-left-radius: 0.3rem;
            border-top-right-radius: 0.3rem;
            padding-top: 8px;
            position: relative;
          }

          .react-datepicker__year-dropdown-container--select,
          .react-datepicker__month-dropdown-container--select,
          .react-datepicker__year-dropdown-container--scroll,
          .react-datepicker__month-dropdown-container--scroll {
            display: inline-block;
            margin: 0 2px;
          }

          .react-datepicker__current-month {
            margin-top: 0;
            color: #000;
            font-weight: bold;
            font-size: 0.944rem;
          }

          .react-datepicker__navigation {
            line-height: 1.7rem;
            text-align: center;
            cursor: pointer;
            position: absolute;
            top: 10px;
            height: 0.2em;
            width: 0;
            border: 0.45rem solid transparent;
            z-index: 1;
          }
          .react-datepicker__navigation--previous {
            left: 10px;
            border-right-color: #ccc;
          }
          .react-datepicker__navigation--previous:hover {
            border-right-color: #b3b2b2;
          }
          .react-datepicker__navigation--next {
            right: 10px;
            border-left-color: #ccc;
          }
          .react-datepicker__navigation--next:hover {
            border-left-color: #b3b2b2;
          }
          .react-datepicker__navigation--years {
            position: relative;
            top: 0;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          .react-datepicker__navigation--years-previous {
            top: 4px;
            border-top-color: #ccc;
          }
          .react-datepicker__navigation--years-previous:hover {
            border-top-color: #b3b2b2;
          }
          .react-datepicker__navigation--years-upcoming {
            top: -4px;
            border-bottom-color: #ccc;
          }
          .react-datepicker__navigation--years-upcoming:hover {
            border-bottom-color: #b3b2b2;
          }
          .react-datepicker__time-container {
            display: inline-block;
            float: right;
            overflow: scroll;
          }
          .react-datepicker__time-list {
            padding-inline-start: 5px;
          }
          .react-datepicker__time-list-item {
            list-style-type: none;
            cursor: pointer;
          }
          .react-datepicker__month-container {
            display: inline;
            float: left;
          }

          .react-datepicker__month {
            margin: 0.4rem;
            text-align: center;
            min-height: 200px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .react-datepicker__week {
            flex-grow: 1;
          }

          .react-datepicker__week-number {
            color: #ccc;
            display: inline-block;
            width: 1.7rem;
            line-height: 1.7rem;
            text-align: center;
            margin: 0.166rem;
          }

          .react-datepicker__day-name,
          .react-datepicker__day {
            color: #000;
            display: inline-block;
            width: 1.7rem;
            line-height: 1.7rem;
            text-align: center;
            margin: 0.166rem;
          }

          .react-datepicker__day {
            cursor: pointer;
          }
          .react-datepicker__day:hover {
            border-radius: 0.3rem;
            background-color: #f0f0f0;
          }
          .react-datepicker__day--today {
            font-weight: bold;
          }
          .react-datepicker__day--highlighted {
            border-radius: 0.3rem;
            background-color: #3dcc4a;
            color: #fff;
          }
          .react-datepicker__day--highlighted:hover {
            background-color: #32be3f;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--in-selecting-range,
          .react-datepicker__day--in-range {
            border-radius: 0.3rem;
            background-color: #216ba5;
            color: #fff;
          }
          .react-datepicker__day--selected:hover,
          .react-datepicker__day--in-selecting-range:hover,
          .react-datepicker__day--in-range:hover {
            background-color: #1d5d90;
          }
          .react-datepicker__day--keyboard-selected {
            border-radius: 0.3rem;
            background-color: #2a87d0;
            color: #fff;
          }
          .react-datepicker__day--keyboard-selected:hover {
            background-color: #1d5d90;
          }
          .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range) {
            background-color: rgba(33, 107, 165, 0.5);
          }
          .react-datepicker__month--selecting-range
            .react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range) {
            background-color: #f0f0f0;
            color: #000;
          }
          .react-datepicker__day--disabled {
            cursor: default;
            color: #ccc;
          }
          .react-datepicker__day--disabled:hover {
            background-color: transparent;
          }

          .react-datepicker__input-container {
            position: relative;
            display: inline-block;
          }

          .react-datepicker__year-read-view,
          .react-datepicker__month-read-view {
            border: 1px solid transparent;
            border-radius: 0.3rem;
          }
          .react-datepicker__year-read-view:hover,
          .react-datepicker__month-read-view:hover {
            cursor: pointer;
          }
          .react-datepicker__year-read-view:hover .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__year-read-view:hover .react-datepicker__month-read-view--down-arrow,
          .react-datepicker__month-read-view:hover .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view:hover .react-datepicker__month-read-view--down-arrow {
            border-top-color: #b3b2b2;
          }
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow {
            border-top-color: #ccc;
            float: right;
            margin-left: 20px;
            top: 8px;
            position: relative;
            border-width: 0.45rem;
          }

          .react-datepicker__year-dropdown,
          .react-datepicker__month-dropdown {
            background-color: #f0f0f0;
            position: absolute;
            width: 50%;
            left: 25%;
            top: 30px;
            text-align: center;
            border-radius: 0.3rem;
            border: 1px solid #aeaeae;
          }
          .react-datepicker__year-dropdown:hover,
          .react-datepicker__month-dropdown:hover {
            cursor: pointer;
          }
          .react-datepicker__year-dropdown--scrollable,
          .react-datepicker__month-dropdown--scrollable {
            height: 150px;
            overflow-y: scroll;
          }

          .react-datepicker__year-option,
          .react-datepicker__month-option {
            line-height: 20px;
            width: 100%;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          .react-datepicker__year-option:first-of-type,
          .react-datepicker__month-option:first-of-type {
            border-top-left-radius: 0.3rem;
            border-top-right-radius: 0.3rem;
          }
          .react-datepicker__year-option:last-of-type,
          .react-datepicker__month-option:last-of-type {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            border-bottom-left-radius: 0.3rem;
            border-bottom-right-radius: 0.3rem;
          }
          .react-datepicker__year-option:hover,
          .react-datepicker__month-option:hover {
            background-color: #ccc;
          }
          .react-datepicker__year-option:hover .react-datepicker__navigation--years-upcoming,
          .react-datepicker__month-option:hover .react-datepicker__navigation--years-upcoming {
            border-bottom-color: #b3b2b2;
          }
          .react-datepicker__year-option:hover .react-datepicker__navigation--years-previous,
          .react-datepicker__month-option:hover .react-datepicker__navigation--years-previous {
            border-top-color: #b3b2b2;
          }
          .react-datepicker__year-option--selected,
          .react-datepicker__month-option--selected {
            position: absolute;
            left: 15px;
          }

          .react-datepicker__close-icon {
            background-color: transparent;
            border: 0;
            cursor: pointer;
            display: inline-block;
            height: 0;
            outline: 0;
            padding: 0;
            vertical-align: middle;
          }
          .react-datepicker__close-icon::after {
            background-color: #216ba5;
            border-radius: 50%;
            bottom: 0;
            box-sizing: border-box;
            color: #fff;
            content: '\\00d7';
            cursor: pointer;
            font-size: 12px;
            height: 16px;
            width: 16px;
            line-height: 1;
            margin: -8px auto 0;
            padding: 2px;
            position: absolute;
            right: 7px;
            text-align: center;
            top: 50%;
          }

          .react-datepicker__today-button {
            background: #f0f0f0;
            border-top: 1px solid #aeaeae;
            cursor: pointer;
            text-align: center;
            font-weight: bold;
            padding: 5px 0;
            clear: left;
          }

          .react-datepicker__tether-element {
            z-index: 2147483647;
          }

          .react-datepicker__portal {
            position: fixed;
            width: 100vw;
            height: 100vh;
            background-color: rgba(0, 0, 0, 0.8);
            left: 0;
            top: 0;
            justify-content: center;
            align-items: center;
            display: flex;
            z-index: 2147483647;
          }
          .react-datepicker__portal .react-datepicker__day-name,
          .react-datepicker__portal .react-datepicker__day {
            width: 3rem;
            line-height: 3rem;
          }
          @media (max-width: 400px), (max-height: 550px) {
            .react-datepicker__portal .react-datepicker__day-name,
            .react-datepicker__portal .react-datepicker__day {
              width: 2rem;
              line-height: 2rem;
            }
          }
          .react-datepicker__portal .react-datepicker__current-month {
            font-size: 1.44rem;
          }
          .react-datepicker__portal .react-datepicker__navigation {
            border: 0.81rem solid transparent;
          }
          .react-datepicker__portal .react-datepicker__navigation--previous {
            border-right-color: #ccc;
          }
          .react-datepicker__portal .react-datepicker__navigation--previous:hover {
            border-right-color: #b3b2b2;
          }
          .react-datepicker__portal .react-datepicker__navigation--next {
            border-left-color: #ccc;
          }
          .react-datepicker__portal .react-datepicker__navigation--next:hover {
            border-left-color: #b3b2b2;
          }
        `}</style>
        {this.renderCustomCSS()}
      </div>
    );
  }
}
